import React, { useEffect, useRef, useMemo } from "react";
import { Container } from "@pixi/react";
import { useDispatch, useSelector } from "react-redux";
import {
  setErrorRecoveryTransitionActive,
  setErrorRecoveryScreenCovered,
  setBet,
} from "../../../State/actions";
import {
  selectErrorRecoveryTransitionActive,
  selectCurrentRound,
} from "../../../State/selectors";
import { useDimensions } from "../../../Contexts/DimensionsContext";
import { useReelControl } from "../../../Contexts/ReelControlContext";
import StorageManager from "../../../utils/StorageManager";
import anime from "animejs";
import * as PIXI from "pixi.js";

const ErrorRecoveryOverlay = () => {
  const dispatch = useDispatch();
  const { dimensions } = useDimensions();
  const { restoreReels } = useReelControl();
  const overlayRef = useRef(null);
  const overlayGraphicsRef = useRef(null);
  const animationRef = useRef(null);
  const animationRunningRef = useRef(false);
  const errorRecoveryTransitionActive = useSelector(
    selectErrorRecoveryTransitionActive,
  );
  const currentRound = useSelector(selectCurrentRound);
  // Keep a ref so animation callbacks always read the latest currentRound,
  // even if refreshGameData updates it after the animation has already started.
  const currentRoundRef = useRef(currentRound);
  currentRoundRef.current = currentRound;

  // Memoize container zIndex
  const containerZIndex = useMemo(() => 10000, []); // Above absolutely everything

  /**
   * Create gradient texture matching the loader background
   */
  const createGradientTexture = (width, height) => {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");

    // Diagonal linear gradient — matches loader
    const linear = ctx.createLinearGradient(0, 0, width, height);
    linear.addColorStop(0, "#2a1f5e");
    linear.addColorStop(0.4, "#150e3a");
    linear.addColorStop(1, "#050020");
    ctx.fillStyle = linear;
    ctx.fillRect(0, 0, width, height);

    return PIXI.Texture.from(canvas);
  };

  // Initialize overlay sprite once on mount
  useEffect(() => {
    if (overlayRef.current && !overlayGraphicsRef.current) {
      const texture = createGradientTexture(dimensions.width, dimensions.height);
      const sprite = new PIXI.Sprite(texture);
      sprite.width = dimensions.width;
      sprite.height = dimensions.height;

      overlayRef.current.addChild(sprite);
      overlayGraphicsRef.current = sprite;

      // Ensure container starts invisible
      overlayRef.current.alpha = 0;
    }

    return () => {
      if (overlayGraphicsRef.current) {
        if (overlayGraphicsRef.current.texture) {
          overlayGraphicsRef.current.texture.destroy(true);
        }
        overlayGraphicsRef.current.destroy();
        overlayGraphicsRef.current = null;
      }
    };
  }, [dimensions.width, dimensions.height]);

  // Update overlay dimensions when dimensions change
  useEffect(() => {
    if (overlayGraphicsRef.current) {
      // Destroy old texture
      if (overlayGraphicsRef.current.texture) {
        overlayGraphicsRef.current.texture.destroy(true);
      }
      // Create new gradient texture at new dimensions
      const texture = createGradientTexture(dimensions.width, dimensions.height);
      overlayGraphicsRef.current.texture = texture;
      overlayGraphicsRef.current.width = dimensions.width;
      overlayGraphicsRef.current.height = dimensions.height;
    }
  }, [dimensions.width, dimensions.height]);

  // Handle error recovery transition animation
  useEffect(() => {
    if (
      errorRecoveryTransitionActive &&
      overlayRef.current &&
      !animationRunningRef.current
    ) {
      animationRunningRef.current = true;

      // Clear any existing animation
      if (animationRef.current) {
        animationRef.current.pause();
      }

      // Create transition: fade in black → hold → restore & setup → fade out
      const animation = anime.timeline({
        complete: () => {
          animationRunningRef.current = false;
          dispatch(setErrorRecoveryTransitionActive(false));
        },
      });

      // Phase 1: Fade in overlay (200ms, matching WarningPopup/MenuOverlay)
      animation.add({
        targets: overlayRef.current,
        alpha: [0, 1],
        duration: 200,
        easing: "easeInQuad",
        complete: () => {
          // Screen is now covered - signal that UI can safely render
          dispatch(setErrorRecoveryScreenCovered(true));
        },
      });

      // Phase 2: Hold overlay and restore reels (200ms)
      animation.add({
        targets: overlayRef.current,
        alpha: 1,
        duration: 200,
        complete: () => {
          // Screen is 100% black - restore reels behind the overlay.
          // Read from ref so we always get the latest currentRound, even if
          // refreshGameData resolved after this animation started.
          const round = currentRoundRef.current;
          let reelsToRestore = null;

          if (
            round?.reels &&
            Array.isArray(round.reels) &&
            round.reels.length > 0
          ) {
            reelsToRestore = round.reels;
          }

          if (reelsToRestore) {
            const baseGame = StorageManager.loadBaseGameRound();
            const storedBet = baseGame?.bet ?? round?.bet;
            restoreReels(reelsToRestore, storedBet);
            
            // Switch to free rounds bet if free rounds are active (screen is black, invisible to user)
            const freeRound = StorageManager.loadFreeRound();
            if (freeRound && freeRound.bet) {
              dispatch(setBet(freeRound.bet));
            }
          }
        },
      });

      // Phase 3: Fade out to reveal restored reels (200ms, matching WarningPopup)
      animation.add({
        targets: overlayRef.current,
        alpha: [1, 0],
        duration: 200,
        easing: "easeOutQuad",
        complete: () => {
          // Reset screen covered flag
          dispatch(setErrorRecoveryScreenCovered(false));
        },
      });

      animationRef.current = animation;
    }
  }, [errorRecoveryTransitionActive, dispatch, restoreReels]);

  // Cleanup only on unmount
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        animationRef.current.pause();
        animationRef.current = null;
      }
    };
  }, []);

  // Always render, visibility controlled by alpha
  return (
    <Container
      name="error_recovery_overlay"
      ref={overlayRef}
      x={0}
      y={0}
      zIndex={containerZIndex}
    />
  );
};

export default ErrorRecoveryOverlay;
